import { useEffect, useRef, useState } from "react";
import QRCodeStyling, {
  type DotType,
  type CornerSquareType,
  type CornerDotType,
} from "qr-code-styling";
import { saveAs } from "file-saver";
import { useLogoStore } from "../state/logoStore";
import { PageLabel } from "../components/ui/PageLabel";

const PREVIEW_PX = 320;
const EXPORT_PX = 1024;

// === SCAN FRAME PRESETS ============================================
// Each style defines a viewBox (the framed graphic's natural aspect ratio)
// and where the QR sits inside it (`qr` rect in viewBox coords). The bg/fg
// markup are SVG strings parameterized by the chosen color.
type ScanFrameStyle = "none" | "below" | "top" | "side";

const SCAN_FRAME_PRESETS: Array<{ style: ScanFrameStyle; label: string }> = [
  { style: "none",  label: "None" },
  { style: "below", label: "Tag below" },
  { style: "top",   label: "Tag top" },
  { style: "side",  label: "Tag side" },
];

const SCAN_COLOR_SWATCHES: Array<{ name: string; hex: string }> = [
  { name: "Ink",     hex: "#1A1A1A" },
  { name: "Sky",     hex: "#3FA9F5" },
  { name: "Royal",   hex: "#1E40AF" },
  { name: "Crimson", hex: "#E11D48" },
  { name: "Forest",  hex: "#10B981" },
  { name: "Marigold", hex: "#F59E0B" },
];

interface FrameDef {
  /** Viewbox the frame is drawn in (controls overall aspect of the framed graphic). */
  vb: { w: number; h: number };
  /** Where the QR sits inside the viewBox (also where the inner white panel is drawn). */
  qr: { x: number; y: number; w: number; h: number };
  /** Markup behind the QR (outer shape + inner white area). */
  bg: (color: string) => string;
  /** Markup in front of the QR (corner brackets + label text). */
  fg: (color: string) => string;
}

function bracketsAround(rect: { x: number; y: number; w: number; h: number }, color: string, sw = 6): string {
  // Slightly inset from inner corners so they sit cleanly on the white panel.
  const inset = 6;
  const arm = Math.max(20, Math.min(rect.w, rect.h) * 0.12);
  const x1 = rect.x + inset, y1 = rect.y + inset;
  const x2 = rect.x + rect.w - inset, y2 = rect.y + rect.h - inset;
  const opts = `stroke="${color}" stroke-width="${sw}" fill="none" stroke-linecap="square" stroke-linejoin="miter"`;
  return [
    `<path d="M ${x1} ${y1 + arm} L ${x1} ${y1} L ${x1 + arm} ${y1}" ${opts}/>`,
    `<path d="M ${x2 - arm} ${y1} L ${x2} ${y1} L ${x2} ${y1 + arm}" ${opts}/>`,
    `<path d="M ${x1} ${y2 - arm} L ${x1} ${y2} L ${x1 + arm} ${y2}" ${opts}/>`,
    `<path d="M ${x2 - arm} ${y2} L ${x2} ${y2} L ${x2} ${y2 - arm}" ${opts}/>`,
  ].join("");
}

function roundedRectPath(x: number, y: number, w: number, h: number, r: number): string {
  return `M ${x + r} ${y} L ${x + w - r} ${y} Q ${x + w} ${y} ${x + w} ${y + r} L ${x + w} ${y + h - r} Q ${x + w} ${y + h} ${x + w - r} ${y + h} L ${x + r} ${y + h} Q ${x} ${y + h} ${x} ${y + h - r} L ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} Z`;
}

const FRAME_DEFS: Record<Exclude<ScanFrameStyle, "none">, FrameDef> = {
  // --- Tag below: rounded rect with QR on top, "SCAN ME" label underneath, small downward tail.
  below: {
    vb: { w: 256, h: 320 },
    qr: { x: 18, y: 18, w: 220, h: 220 },
    bg: (c) => {
      const w = 256, h = 300, r = 22, cx = w / 2, tabW = 28, tabH = 16;
      const outer = `M ${r} 0 L ${w - r} 0 Q ${w} 0 ${w} ${r} L ${w} ${h - r} Q ${w} ${h} ${w - r} ${h} L ${cx + tabW / 2} ${h} L ${cx} ${h + tabH} L ${cx - tabW / 2} ${h} L ${r} ${h} Q 0 ${h} 0 ${h - r} L 0 ${r} Q 0 0 ${r} 0 Z`;
      return `<path d="${outer}" fill="${c}"/>` +
             `<path d="${roundedRectPath(18, 18, 220, 220, 8)}" fill="#ffffff"/>`;
    },
    fg: (c) => bracketsAround({ x: 18, y: 18, w: 220, h: 220 }, c) +
               `<text x="128" y="278" text-anchor="middle" font-family="Roboto, -apple-system, system-ui, sans-serif" font-size="28" font-weight="800" letter-spacing="2" fill="#ffffff">SCAN ME</text>`,
  },

  // --- Tag top: "SCAN HERE!" header on top, QR below.
  top: {
    vb: { w: 256, h: 320 },
    qr: { x: 18, y: 80, w: 220, h: 220 },
    bg: (c) => {
      const w = 256, h = 320, r = 22;
      // Outer rounded rect, full size
      const outer = roundedRectPath(0, 0, w, h, r);
      // Inner white area for QR
      const inner = roundedRectPath(18, 80, 220, 220, 8);
      return `<path d="${outer}" fill="${c}"/><path d="${inner}" fill="#ffffff"/>`;
    },
    fg: (c) => bracketsAround({ x: 18, y: 80, w: 220, h: 220 }, c) +
               `<text x="128" y="48" text-anchor="middle" font-family="Roboto, -apple-system, system-ui, sans-serif" font-size="28" font-weight="800" letter-spacing="2" fill="#ffffff">SCAN HERE!</text>`,
  },

  // --- Tag side: pill on the left ("SCAN ME!"), arrow pointing right at the QR.
  side: {
    vb: { w: 460, h: 220 },
    qr: { x: 230, y: 10, w: 200, h: 200 },
    bg: (c) => {
      // Left pill: rounded rect 0..200 x 30..190
      const pill = roundedRectPath(0, 30, 200, 160, 80);
      // Arrow tail: triangle from pill right edge to QR left edge
      const arrow = `M 195 90 L 235 110 L 195 130 Z`;
      // QR card: rounded rect on the right
      const card = roundedRectPath(230, 10, 200, 200, 18);
      // White inner panel inside QR card
      const innerCard = roundedRectPath(240, 20, 180, 180, 12);
      return `<path d="${pill}" fill="${c}"/>` +
             `<path d="${arrow}" fill="${c}"/>` +
             `<path d="${card}" fill="${c}"/>` +
             `<path d="${innerCard}" fill="#ffffff"/>`;
    },
    fg: (c) => bracketsAround({ x: 240, y: 20, w: 180, h: 180 }, c, 5) +
               `<text x="100" y="120" text-anchor="middle" font-family="Roboto, -apple-system, system-ui, sans-serif" font-size="40" font-weight="900" letter-spacing="2" fill="#ffffff">SCAN ME!</text>`,
  },
};

// === PAGE: QR MAKER (rich generator with custom dots, corners, embedded logo) ===

const DOT_OPTIONS: Array<{ value: DotType; label: string }> = [
  { value: "square",          label: "Square" },
  { value: "rounded",         label: "Rounded" },
  { value: "dots",            label: "Dots" },
  { value: "extra-rounded",   label: "Soft" },
  { value: "classy",          label: "Classy" },
  { value: "classy-rounded",  label: "Classy round" },
];
const CORNER_SQ_OPTIONS: Array<{ value: CornerSquareType; label: string }> = [
  { value: "square",        label: "Square" },
  { value: "extra-rounded", label: "Rounded" },
  { value: "dot",           label: "Circle" },
];
const CORNER_DOT_OPTIONS: Array<{ value: CornerDotType; label: string }> = [
  { value: "square", label: "Square" },
  { value: "dot",    label: "Circle" },
];

export function QrMakerPage() {
  const setPage = useLogoStore((s) => s.setPage);

  const [url, setUrl] = useState("https://www.needeyeexam.com");
  const [fg, setFg] = useState("#1A1A1A");
  const [bg, setBg] = useState("#FFFFFF");
  const [dots, setDots] = useState<DotType>("rounded");
  const [cornerSq, setCornerSq] = useState<CornerSquareType>("extra-rounded");
  const [cornerDot, setCornerDot] = useState<CornerDotType>("square");
  const [logo, setLogo] = useState<{ dataUrl: string; name: string } | null>(null);
  const [logoSize, setLogoSize] = useState(35); // percent
  const [bgImage, setBgImage] = useState<{ dataUrl: string; name: string } | null>(null);
  // % of background image visibility — lower = more white-overlay = QR is more dominant.
  // Default 35 keeps QR dark and scannable while the image still reads through.
  const [bgImageVisibility, setBgImageVisibility] = useState(35);
  const [scanFrameStyle, setScanFrameStyle] = useState<ScanFrameStyle>("none");
  const [scanFrameColor, setScanFrameColor] = useState<string>("#1A1A1A");

  const stageRef = useRef<HTMLDivElement | null>(null);
  const qrRef = useRef<QRCodeStyling | null>(null);

  // Build the option payload qr-code-styling expects.
  function buildOptions() {
    // When a background image is set, force the QR's own background to be
    // transparent so the image (and white veil) show through. Also bump error
    // correction to H so the dots still scan reliably over the busier surface.
    const hasBgImage = !!bgImage;
    const opts = {
      width: PREVIEW_PX,
      height: PREVIEW_PX,
      type: "svg" as const,
      data: url || " ",
      margin: 8,
      qrOptions: {
        errorCorrectionLevel: (logo || hasBgImage ? "H" : "Q") as "L" | "M" | "Q" | "H",
      },
      dotsOptions:          { color: fg, type: dots },
      backgroundOptions:    { color: hasBgImage ? "transparent" : bg },
      cornersSquareOptions: { color: fg, type: cornerSq },
      cornersDotOptions:    { color: fg, type: cornerDot },
      ...(logo && {
        image: logo.dataUrl,
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: logoSize / 100,
          margin: 6,
          crossOrigin: "anonymous",
        },
      }),
    };
    return opts;
  }

  // Mount the QR instance once into the stage.
  useEffect(() => {
    if (!stageRef.current) return;
    const qr = new QRCodeStyling(buildOptions());
    qrRef.current = qr;
    stageRef.current.replaceChildren(); // wipe any prior mount
    qr.append(stageRef.current);
    return () => {
      qrRef.current = null;
      if (stageRef.current) stageRef.current.replaceChildren();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-render the QR whenever any input changes.
  useEffect(() => {
    qrRef.current?.update(buildOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, fg, bg, dots, cornerSq, cornerDot, logo, logoSize, bgImage, bgImageVisibility]);

  // Frame-aware geometry: when a frame style is active, the framed graphic has
  // its own aspect ratio (defined by FRAME_DEFS). The preview stage adopts that
  // ratio at a fixed display height, and the QR mount sits inside the frame's
  // designated QR area.
  const frameDef = scanFrameStyle !== "none" ? FRAME_DEFS[scanFrameStyle] : null;
  const stageHeight = 360;
  const stageWidth = frameDef
    ? Math.round((stageHeight * frameDef.vb.w) / frameDef.vb.h)
    : 320;
  const qrMountStyle: React.CSSProperties | undefined = frameDef
    ? {
        position: "absolute",
        left: `${(frameDef.qr.x / frameDef.vb.w) * 100}%`,
        top: `${(frameDef.qr.y / frameDef.vb.h) * 100}%`,
        width: `${(frameDef.qr.w / frameDef.vb.w) * 100}%`,
        height: `${(frameDef.qr.h / frameDef.vb.h) * 100}%`,
      }
    : undefined;
  const innerAreaInset = qrMountStyle; // bg image + veil constrained to QR area when frame is on

  function readImageFile(
    file: File | undefined,
    onLoad: (payload: { dataUrl: string; name: string }) => void,
  ) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result;
      if (typeof result === "string") onLoad({ dataUrl: result, name: file.name });
    };
    reader.readAsDataURL(file);
  }

  /** Load an Image and resolve once decoded (or rejected on error). */
  function loadHTMLImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = src;
    });
  }

  // White overlay alpha = (1 - visibility%). Higher visibility = thinner overlay.
  function overlayAlpha(): number {
    return Math.max(0, Math.min(1, 1 - bgImageVisibility / 100));
  }

  /**
   * Build a single composed SVG that includes (in z-order):
   *   1. Frame back layer (outer shape + inner white panel) — if frame on
   *   2. BG image clipped to QR area + white veil — if bg on
   *   3. QR (extracted from qr-code-styling, scaled into QR area)
   *   4. Frame front layer (corner brackets + label) — if frame on
   * Returns the complete <svg>…</svg> string ready to download or rasterize.
   */
  async function buildComposedSvg(): Promise<{ svg: string; w: number; h: number }> {
    const qrBlob = await qrRef.current?.getRawData("svg");
    const qrSvgText = qrBlob instanceof Blob ? await qrBlob.text() : "";
    const qrInner = qrSvgText
      .replace(/^<\?xml[^?]*\?>\s*/i, "")
      .replace(/<svg[^>]*>/i, "")
      .replace(/<\/svg>\s*$/i, "");

    // No frame, no image → return the pristine QR SVG.
    if (!frameDef && !bgImage) {
      return { svg: qrSvgText, w: PREVIEW_PX, h: PREVIEW_PX };
    }

    // Determine viewBox + QR area.
    const vb = frameDef ? frameDef.vb : { w: PREVIEW_PX, h: PREVIEW_PX };
    const qrArea = frameDef
      ? frameDef.qr
      : { x: 0, y: 0, w: PREVIEW_PX, h: PREVIEW_PX };

    const veilAlpha = overlayAlpha().toFixed(3);
    const clipId = `qr-clip-${Math.random().toString(36).slice(2, 8)}`;
    const scale = qrArea.w / PREVIEW_PX;

    const layers: string[] = [];

    if (frameDef) {
      layers.push(frameDef.bg(scanFrameColor));
    }

    if (bgImage) {
      // Clip the bg image to the QR area so framed cases don't bleed it.
      layers.push(
        `<defs><clipPath id="${clipId}"><rect x="${qrArea.x}" y="${qrArea.y}" width="${qrArea.w}" height="${qrArea.h}" rx="6" ry="6"/></clipPath></defs>`,
        `<g clip-path="url(#${clipId})">`,
        `<image href="${bgImage.dataUrl}" x="${qrArea.x}" y="${qrArea.y}" width="${qrArea.w}" height="${qrArea.h}" preserveAspectRatio="xMidYMid slice"/>`,
        `<rect x="${qrArea.x}" y="${qrArea.y}" width="${qrArea.w}" height="${qrArea.h}" fill="rgb(255,255,255)" fill-opacity="${veilAlpha}"/>`,
        `</g>`,
      );
    }

    // QR — translate + scale into the QR area.
    layers.push(
      `<g transform="translate(${qrArea.x} ${qrArea.y}) scale(${scale})">${qrInner}</g>`,
    );

    if (frameDef) {
      layers.push(frameDef.fg(scanFrameColor));
    }

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}">` +
      layers.join("") +
      `</svg>`;
    return { svg, w: vb.w, h: vb.h };
  }

  async function downloadSvg() {
    const { svg } = await buildComposedSvg();
    const blob = new Blob([svg], { type: "image/svg+xml" });
    saveAs(blob, "qr-code.svg");
  }

  async function downloadPng() {
    const { svg, w: vbW, h: vbH } = await buildComposedSvg();
    // Scale the export so the longest side is EXPORT_PX, preserving aspect.
    const aspect = vbW / vbH;
    const exportW = aspect >= 1 ? EXPORT_PX : Math.round(EXPORT_PX * aspect);
    const exportH = aspect >= 1 ? Math.round(EXPORT_PX / aspect) : EXPORT_PX;
    const canvas = document.createElement("canvas");
    canvas.width = exportW;
    canvas.height = exportH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    try {
      const img = await loadHTMLImage(url);
      ctx.drawImage(img, 0, 0, exportW, exportH);
    } finally {
      URL.revokeObjectURL(url);
    }
    canvas.toBlob((b) => {
      if (b) saveAs(b, "qr-code.png");
    }, "image/png");
  }

  return (
    <div className="page qr-maker">
      <PageLabel name="QR MAKER" />
      <header className="topbar">
        <button className="btn pill-nav pill-nav-back" onClick={() => setPage("start")}>
          <span className="pill-nav-arrow" aria-hidden="true">←</span>
          <span>Home</span>
        </button>
      </header>

      <div className="qr-maker-banner" role="img" aria-label="QR Maker banner" />

      <div style={{ padding: "20px 24px 80px", maxWidth: 1080, margin: "0 auto" }}>
        <div className="qrm-grid">
          {/* ===== CONTROLS ===== */}
          <div className="qrm-card qrm-controls">
            <h3 className="qrm-card-title">Customize</h3>
            <Field label="Destination URL">
              <input
                type="url"
                className="qrm-url"
                value={url}
                placeholder="https://example.com"
                onChange={(e) => setUrl(e.target.value)}
              />
            </Field>

            <Field label="Colors">
              <div className="qrm-color-row">
                <ColorField name="Foreground" value={fg} onChange={setFg} />
                <ColorField name="Background" value={bg} onChange={setBg} />
              </div>
            </Field>

            <Field label="Dot style" hint="— the body of the code">
              <Seg options={DOT_OPTIONS} value={dots} onChange={setDots} rows={2} />
            </Field>

            <Field label="Corner frame" hint="— the three big squares">
              <Seg options={CORNER_SQ_OPTIONS} value={cornerSq} onChange={setCornerSq} />
            </Field>

            <Field label="Corner dot" hint="— the dot inside each frame">
              <Seg options={CORNER_DOT_OPTIONS} value={cornerDot} onChange={setCornerDot} />
            </Field>

            <Field label="Logo">
              <label className="qrm-logo-zone">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={(e) => readImageFile(e.target.files?.[0], setLogo)}
                />
                {!logo ? (
                  <div className="qrm-logo-empty">
                    Drop or <span className="qrm-accent">browse</span> for an image · PNG, JPG, SVG
                  </div>
                ) : (
                  <div className="qrm-logo-preview">
                    <img className="qrm-logo-thumb" src={logo.dataUrl} alt="" />
                    <div className="qrm-logo-info">
                      <div>Logo applied</div>
                      <div className="qrm-logo-name">{logo.name}</div>
                    </div>
                    <button
                      type="button"
                      className="qrm-logo-remove"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLogo(null); }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </label>
            </Field>

            {logo && (
              <Field label="Logo size">
                <div className="qrm-slider-row">
                  <input
                    type="range"
                    min={20}
                    max={50}
                    step={1}
                    value={logoSize}
                    onChange={(e) => setLogoSize(Number(e.target.value))}
                  />
                  <div className="qrm-slider-value">{logoSize}%</div>
                </div>
              </Field>
            )}

            <Field label="Background image" hint="— sits behind the QR, mildly transparent">
              <label className="qrm-logo-zone">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={(e) => readImageFile(e.target.files?.[0], setBgImage)}
                />
                {!bgImage ? (
                  <div className="qrm-logo-empty">
                    Drop or <span className="qrm-accent">browse</span> for a photo · the QR overlays it
                  </div>
                ) : (
                  <div className="qrm-logo-preview">
                    <img className="qrm-logo-thumb" src={bgImage.dataUrl} alt="" />
                    <div className="qrm-logo-info">
                      <div>Background applied</div>
                      <div className="qrm-logo-name">{bgImage.name}</div>
                    </div>
                    <button
                      type="button"
                      className="qrm-logo-remove"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBgImage(null); }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </label>
            </Field>

            {bgImage && (
              <Field label="Image visibility" hint="— lower keeps QR more dominant & scannable">
                <div className="qrm-slider-row">
                  <input
                    type="range"
                    min={10}
                    max={70}
                    step={1}
                    value={bgImageVisibility}
                    onChange={(e) => setBgImageVisibility(Number(e.target.value))}
                  />
                  <div className="qrm-slider-value">{bgImageVisibility}%</div>
                </div>
              </Field>
            )}

            <Field label="Scan-me frame" hint="— sticker-style border around the QR">
              <Seg
                options={SCAN_FRAME_PRESETS.map((p) => ({ value: p.style, label: p.label }))}
                value={scanFrameStyle}
                onChange={setScanFrameStyle}
              />
            </Field>

            {scanFrameStyle !== "none" && (
              <Field label="Frame color">
                <div className="qrm-swatch-row">
                  {SCAN_COLOR_SWATCHES.map((s) => (
                    <button
                      key={s.hex}
                      type="button"
                      className={`qrm-swatch ${scanFrameColor.toLowerCase() === s.hex.toLowerCase() ? "active" : ""}`}
                      style={{ background: s.hex }}
                      onClick={() => setScanFrameColor(s.hex)}
                      title={s.name}
                      aria-label={s.name}
                    />
                  ))}
                  <label className="qrm-swatch qrm-swatch-custom" title="Custom color">
                    <input
                      type="color"
                      value={scanFrameColor}
                      onChange={(e) => setScanFrameColor(e.target.value.toUpperCase())}
                    />
                    <span aria-hidden="true">+</span>
                  </label>
                </div>
              </Field>
            )}
          </div>

          {/* ===== PREVIEW ===== */}
          <div className="qrm-card qrm-preview-wrap">
            <h3 className="qrm-card-title">Preview</h3>
            <div className="qrm-preview-stagewrap">
              <div
                className="qrm-preview-stage"
                style={{ width: stageWidth, height: stageHeight }}
              >
                {/* Frame back layer (outer shape + inner white panel). */}
                {frameDef && (
                  <svg
                    className="qrm-frame-svg"
                    viewBox={`0 0 ${frameDef.vb.w} ${frameDef.vb.h}`}
                    preserveAspectRatio="xMidYMid meet"
                    dangerouslySetInnerHTML={{ __html: frameDef.bg(scanFrameColor) }}
                  />
                )}
                {/* Optional bg image + veil — confined to the QR area when a frame is on. */}
                {bgImage && (
                  <>
                    <div
                      className="qrm-stage-bg"
                      style={{ backgroundImage: `url(${bgImage.dataUrl})`, ...innerAreaInset }}
                    />
                    <div
                      className="qrm-stage-veil"
                      style={{ opacity: overlayAlpha(), ...innerAreaInset }}
                    />
                  </>
                )}
                {/* QR mount (qr-code-styling appends its DOM here). */}
                <div className="qrm-stage-qr" ref={stageRef} style={qrMountStyle} />
                {/* Frame foreground (corner brackets + label text). */}
                {frameDef && (
                  <svg
                    className="qrm-frame-svg qrm-frame-fg"
                    viewBox={`0 0 ${frameDef.vb.w} ${frameDef.vb.h}`}
                    preserveAspectRatio="xMidYMid meet"
                    dangerouslySetInnerHTML={{ __html: frameDef.fg(scanFrameColor) }}
                  />
                )}
              </div>
            </div>
            <div className="qrm-preview-meta">
              <span>Live preview</span>
              <span>{stageWidth} × {stageHeight}</span>
            </div>
            <div className="qrm-download-row">
              <button type="button" className="btn primary big" onClick={downloadPng}>
                <DownloadIcon /> Download PNG
              </button>
              <button type="button" className="btn ghost" onClick={downloadSvg}>
                <DownloadIcon /> SVG
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========== Helpers ========== */

function Field(props: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="qrm-field-group">
      <div className="qrm-field-label">
        {props.label}
        {props.hint && <span className="qrm-hint">{props.hint}</span>}
      </div>
      {props.children}
    </div>
  );
}

function ColorField(props: { name: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="qrm-color-field">
      <div className="qrm-color-swatch" style={{ background: props.value }}>
        <input
          type="color"
          value={props.value}
          onChange={(e) => props.onChange(e.target.value.toUpperCase())}
        />
      </div>
      <div className="qrm-color-info">
        <div className="qrm-color-name">{props.name}</div>
        <div className="qrm-color-hex">{props.value.toUpperCase()}</div>
      </div>
    </label>
  );
}

function Seg<T extends string>(props: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
  rows?: number;
}) {
  // Optional row split (e.g., dots panel uses 2 rows of 3 buttons).
  const rows = props.rows ?? 1;
  if (rows === 1) {
    return (
      <div className="qrm-seg-row">
        {props.options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`qrm-seg-btn ${props.value === o.value ? "active" : ""}`}
            onClick={() => props.onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  // Split items into N rows
  const perRow = Math.ceil(props.options.length / rows);
  const groups: typeof props.options[] = [];
  for (let i = 0; i < rows; i++) groups.push(props.options.slice(i * perRow, (i + 1) * perRow));
  return (
    <div className="qrm-seg">
      {groups.map((group, i) => (
        <div key={i} className="qrm-seg-row">
          {group.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`qrm-seg-btn ${props.value === o.value ? "active" : ""}`}
              onClick={() => props.onChange(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Draw an HTMLImageElement into a canvas region using cover-fit (no stretch). */
function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  destW: number,
  destH: number,
) {
  const srcRatio = img.width / img.height;
  const destRatio = destW / destH;
  let sx = 0, sy = 0, sW = img.width, sH = img.height;
  if (srcRatio > destRatio) {
    // Image is wider — crop sides
    sW = img.height * destRatio;
    sx = (img.width - sW) / 2;
  } else {
    // Image is taller — crop top/bottom
    sH = img.width / destRatio;
    sy = (img.height - sH) / 2;
  }
  ctx.drawImage(img, sx, sy, sW, sH, 0, 0, destW, destH);
}
